import { RogItemStatus } from '../../../types';

export type RogItemStatusValue = `${RogItemStatus}`;

export type RogPositionResultCamel = {
  finalItemNumber: string | null;
  rogItemStatus: RogItemStatusValue[];
  listOfPrUuids: string[];
};

export const ROG_STATUS_VALUES: RogItemStatusValue[] = [
  RogItemStatus.CORRECT,
  RogItemStatus.MOVE,
  RogItemStatus.OUT,
  RogItemStatus.REMOVE,
  RogItemStatus.NA,
];

export const ROG_STATUS_COLOR: Record<RogItemStatusValue, string> = {
  [RogItemStatus.CORRECT]: '#1a8542ff',
  [RogItemStatus.MOVE]: '#eab308',
  [RogItemStatus.OUT]: '#da4444ff',
  [RogItemStatus.REMOVE]: '#a855f7',
  [RogItemStatus.NA]: '#9ca3af',
};

export const ROG_STATUS_LABEL: Record<RogItemStatusValue, string> = {
  [RogItemStatus.CORRECT]: 'Correct',
  [RogItemStatus.MOVE]: 'Move',
  [RogItemStatus.OUT]: 'Out',
  [RogItemStatus.REMOVE]: 'Remove',
  [RogItemStatus.NA]: 'N/A',
};

const KNOWN_STATUSES = new Set<string>(ROG_STATUS_VALUES);

const isRogStatusValue = (s: string): s is RogItemStatusValue => KNOWN_STATUSES.has(s);

// Backend now emits a list of statuses; legacy MongoDB docs may store a single
// string. Always return a clean array of recognized statuses.
export const coerceStatusList = (raw: unknown): RogItemStatusValue[] => {
  if (raw == null) return [];
  const arr = Array.isArray(raw) ? raw : [raw];
  const out: RogItemStatusValue[] = [];
  for (const v of arr) {
    const s = String(v);
    if (isRogStatusValue(s)) out.push(s);
  }
  return out;
};

export const parsePositionKey = (
  key: string
): { mod: number; shelf: number; column: number } | null => {
  const parts = key.split(',');
  if (parts.length !== 3) return null;
  const mod = Number(parts[0]);
  const shelf = Number(parts[1]);
  const column = Number(parts[2]);
  if (!Number.isFinite(mod) || !Number.isFinite(shelf) || !Number.isFinite(column)) return null;
  return { mod, shelf, column };
};

export type GroupedPosition = {
  column: number;
  key: string;
  value: RogPositionResultCamel;
};

export const groupPositions = (
  record: Record<string, RogPositionResultCamel>
): Map<number, Map<number, GroupedPosition[]>> => {
  const byMod = new Map<number, Map<number, GroupedPosition[]>>();
  for (const [key, value] of Object.entries(record)) {
    const parsed = parsePositionKey(key);
    if (!parsed) continue;
    const { mod, shelf, column } = parsed;
    if (!byMod.has(mod)) byMod.set(mod, new Map());
    const byShelf = byMod.get(mod)!;
    if (!byShelf.has(shelf)) byShelf.set(shelf, []);
    byShelf.get(shelf)!.push({ column, key, value });
  }
  for (const byShelf of byMod.values()) {
    for (const cells of byShelf.values()) {
      cells.sort((a, b) => a.column - b.column);
    }
  }
  return byMod;
};
